import { useData } from "../contexts/DataContext/DataContextt";
import { useCallback } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchOffRoundedIcon from "@mui/icons-material/SearchOffRounded";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import PageContainer from "../components/Forms/PageContainer";
import { useNavigate } from "react-router";
import {
  deleteFeatureGroup,
  fetchFeatureGroups,
} from "../services/featureGroupService";
import { featureGroupsPageCache } from "./cache/PageCache";
import { usePagedQuery } from "../contexts/hooks/usePagedQuery";
import TabulatedGridCollapsibleV2 from "../components/Forms/TabulatedGridCollapsibleV2";
import EditNoteRoundedIcon from "@mui/icons-material/EditNoteRounded";
import DeleteForeverRoundedIcon from "@mui/icons-material/DeleteForeverRounded";
import { RowActions } from "../utils/RowActions";

const FeatureGroupPage = () => {
  const { language } = useData();
  const navigate = useNavigate();

  const {
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
    handleKeyDown,
    handleSearch,
    data: featureGroupData,
    total,
    searchTerm,
    isLoading,
    reload,
    handleDeleteAction,
    searchType,
    setSearchType,
    searchOption,
  } = usePagedQuery({
    fetchFunction: fetchFeatureGroups,
    language,
    searchOption: [
      { id: "featureGroupCode", display: "Code" },
      { id: "featureGroupName", display: "Name" },
    ],
    cache: featureGroupsPageCache,
    deps: [language],
  });

  const headers = [
    {
      prop: "featureGroupCode",
      text: "Code",
      width: 160,
      link: {
        action: (row) =>
          navigate(`/featuregroup/edit/${row.featureGroupCode}`, {
            state: { row },
          }),
      },
    },
    {
      prop: "featureGroupName",
      text: "Name",
    },
    {
      prop: "featureGroupDisplayName",
      text: "Display Name",
    },
  ];

  const pageRowActions = [
    {
      tooltip: "Edit Feature Group",
      icon: <EditNoteRoundedIcon />,
      onClick: (row) =>
        navigate(`/featuregroup/edit/${row.featureGroupCode}`, {
          state: { row },
        }),
    },
    {
      tooltip: "Delete Feature Group",
      icon: <DeleteForeverRoundedIcon />,
      color: "error",
      onClick: (row) => {
        handleDeleteAction(deleteFeatureGroup, row, "featureGroupCode", {
          title: "Delete Feature Group",
          message: `Are you sure you want to delete feature group ${row.featureGroupCode}?`,
        });
      },
    },
  ];

  const handleCreateClick = useCallback(() => {
    navigate("/featuregroups/new");
  }, [navigate]);

  return (
    <PageContainer
      title="ETIM Feature Groups"
      subtitle="Manage ETIM feature groups"
      titleAdornment={
        <Chip
          size="small"
          label={total}
          aria-label={`${total} ${searchTerm ? "matching " : ""}feature groups`}
        />
      }
      breadcrumbs={[{ title: "ETIM Feature Groups" }]}
      actions={
        <Stack direction="row" alignItems="center" spacing={1}>
          <Tooltip
            title="Refresh feature groups"
            placement="bottom"
            enterDelay={300}
          >
            <span>
              <IconButton
                size="small"
                aria-label="Refresh feature groups"
                onClick={() => reload({ force: true })}
                disabled={isLoading}
              >
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Button
            variant="contained"
            onClick={handleCreateClick}
            startIcon={<AddIcon />}
          >
            Create
          </Button>
        </Stack>
      }
    >
      <Box mt={3}>
        <TabulatedGridCollapsibleV2
          variant="modern"
          loading={isLoading}
          emptyState={
            <Stack
              alignItems="center"
              spacing={1.5}
              sx={{ py: 8, color: "text.secondary" }}
            >
              <SearchOffRoundedIcon sx={{ fontSize: 40 }} />
              <Typography>No feature groups found</Typography>
              {searchTerm && (
                <Button size="small" onClick={() => handleSearch("")}>
                  Clear search
                </Button>
              )}
            </Stack>
          }
          pagination={{
            totalRows: total,
            page,
            rowsPerPage,
            handleChangePage,
            handleChangeRowsPerPage,
          }}
          search={{
            placeHolder: "Search feature groups...",
            searchOption,
            searchType,
            setSearchType,
            handleSearchClick: handleSearch,
            onKeyDown: handleKeyDown,
            activeTerm: searchTerm,
            onClear: () => handleSearch(""),
          }}
          data={featureGroupData}
          headers={headers}
          emptyPlaceholder="—"
          rowActions={(row) => (
            <RowActions row={row} actions={pageRowActions} />
          )}
        />
      </Box>
    </PageContainer>
  );
};

export default FeatureGroupPage;
